import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback } from 'react';

import { BaseInputProps } from './StyledTextField';

export interface SelectOption<T = string> {
  value: T;
  label: string;
}

export interface SelectInputProps<T = string> extends BaseInputProps {
  /** Valor seleccionado */
  value: T | null;
  /** Callback cuando cambia el valor */
  onChange: (value: T | null) => void;
  /** Opciones disponibles */
  options: SelectOption<T>[];
  /** Nombre del campo para formularios */
  name?: string;
  /** Tamaño del input */
  size?: 'small' | 'medium';
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderWidth: '1px',
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
    '&.Mui-error fieldset': {
      borderColor: theme.palette.error.main,
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      '& fieldset': {
        borderColor: theme.palette.divider,
        opacity: 0.4,
      },
      '& .MuiSelect-select': {
        color: theme.palette.text.primary,
        opacity: 1,
        WebkitTextFillColor: theme.palette.text.primary,
      },
      '& .MuiSelect-icon': {
        opacity: 0.4,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    transform: 'translate(12px, 9px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '&.Mui-disabled': {
      color: theme.palette.text.primary,
      opacity: 1,
    },
  },
  '&.Mui-disabled .MuiInputLabel-root': {
    color: theme.palette.text.primary,
    opacity: 1,
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.75rem',
    marginTop: '4px',
    marginLeft: '2px',
  },
}));

export function SelectInput<T = string>({
  value,
  onChange,
  options,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  name,
  size = 'small',
}: SelectInputProps<T>): React.ReactElement {
  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const selectedValue = event.target.value;
      if (selectedValue === '') {
        onChange(null);
        return;
      }
      // Buscar la opción correspondiente
      const option = options.find(
        (opt) => String(opt.value) === String(selectedValue),
      );
      onChange(option ? option.value : null);
    },
    [onChange, options],
  );

  // Generar ID único para accesibilidad
  const labelId = label
    ? `select-label-${label.replace(/\s+/g, '-').toLowerCase()}`
    : undefined;

  return (
    <StyledFormControl
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      required={required}
      size={size}
    >
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={labelId}
        value={value !== null ? value : ''}
        onChange={handleChange}
        label={label}
        name={name}
        displayEmpty={!!placeholder}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option, index) => (
          <MenuItem
            key={`${String(option.value)}-${index}`}
            value={option.value as string | number}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
}
